import { useId } from "react";
import { useTranslation } from "react-i18next";

import { Label } from "@/components/ui/label";
import { PasswordField } from "@/components/ui/PasswordField";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export type PasswordMode = "manual" | "generate";

export interface ManualPasswordState {
  password: string;
  confirm: string;
}

interface PasswordChoiceProps {
  mode: PasswordMode;
  onModeChange: (next: PasswordMode) => void;
  manual: ManualPasswordState;
  onManualChange: (next: ManualPasswordState) => void;
  disabled?: boolean;
}

export function PasswordChoice({
  mode,
  onModeChange,
  manual,
  onManualChange,
  disabled,
}: PasswordChoiceProps) {
  const { t } = useTranslation("admin-users");
  const groupLabelId = useId();
  const manualPwId = useId();
  const confirmPwId = useId();
  const manualRadioId = useId();
  const generateRadioId = useId();

  return (
    <div className="flex flex-col gap-3">
      <span
        id={groupLabelId}
        className="text-sm font-medium leading-none"
      >
        {t("passwordChoice.modeLabel")}
      </span>
      <RadioGroup
        value={mode}
        onValueChange={(value) => onModeChange(value as PasswordMode)}
        disabled={disabled}
        aria-labelledby={groupLabelId}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="manual" id={manualRadioId} />
          <Label htmlFor={manualRadioId} className="cursor-pointer text-sm">
            {t("passwordChoice.manualOption")}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="generate" id={generateRadioId} />
          <Label htmlFor={generateRadioId} className="cursor-pointer text-sm">
            {t("passwordChoice.generateOption")}
          </Label>
        </div>
      </RadioGroup>

      {mode === "manual" ? (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2">
            <Label htmlFor={manualPwId}>{t("passwordChoice.newLabel")}</Label>
            <PasswordField
              id={manualPwId}
              autoComplete="new-password"
              required
              minLength={8}
              value={manual.password}
              onChange={(e) =>
                onManualChange({ ...manual, password: e.target.value })
              }
              disabled={disabled}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor={confirmPwId}>{t("passwordChoice.confirmLabel")}</Label>
            <PasswordField
              id={confirmPwId}
              autoComplete="new-password"
              required
              minLength={8}
              value={manual.confirm}
              onChange={(e) =>
                onManualChange({ ...manual, confirm: e.target.value })
              }
              disabled={disabled}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function validateManual(
  manual: ManualPasswordState,
): "passwordTooShort" | "passwordMismatch" | null {
  if (manual.password.length < 8) return "passwordTooShort";
  if (manual.password !== manual.confirm) return "passwordMismatch";
  return null;
}
